/**
 * Pure presentation helpers for the queue rail. Nothing here computes a band
 * or a sort order — that is the operations layer's job (Task 5's
 * computeBand/sortRail). This module only turns a ThreadSummary the rail was
 * already handed, in the order it was already handed, into copy and token
 * classes.
 *
 * Literal copy below (band names, the held-arrival pill, the empty state,
 * the coverage-off consequence, the per-role footer sort line, the
 * multi-dispatcher coverage note) is taken verbatim from
 * docs/design-handoff/prototypes/Communicator Console Prototype.dc.html and
 * docs/design-handoff/README.md "Queue rail (pane A)" — see the doc comments
 * on `railFooter`/`coverageNote` for exact line references.
 */
import type { CSSProperties, KeyboardEvent } from 'react'
import type { Band, CallState, Channel, Liveness, StaffRole, ThreadSummary, Tier } from '@communicator/shared'
import type { Peer } from '../../store'
import type { IconName } from '../ui'

/** Space/Enter activation for the div/span-as-button rail controls (rows, chips, menu items). */
export function activateOnKey(e: KeyboardEvent<HTMLElement>, fn: () => void): void {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    fn()
  }
}

/**
 * The rail's channel tile icon — README §3's "channel tile", replacing the
 * old text glyph above. Names are `Icon` component names (the vendored
 * Lucide set), per the design's fixed channel contract (phone /
 * message-square / message-circle).
 */
export const CHANNEL_ICON: Record<Channel, IconName> = {
  voice: 'phone',
  sms: 'message-square',
  chat: 'message-circle',
}

/**
 * The CSS custom property (declared in every theme in tokens.css) each
 * channel icon renders in, for the STANDALONE-icon case (no matching tile
 * fill beside it) — `ThreadHeader.tsx`'s meta-line icon, `TicketSection.tsx`'s
 * sibling-row icon. No Tailwind utility exists for these, so callers reach
 * for this as an inline `style={{ color: 'var(...)' }}`.
 */
export const CHANNEL_ICON_VAR: Record<Channel, string> = {
  voice: '--channel-phone',
  sms: '--channel-sms',
  chat: '--channel-chat',
}

/**
 * The channel tile's slot — voice/chat take the primary tint, sms the accent
 * tint, matching the design's own "phone and chat share the primary tint"
 * contract. See `channelTileStyle` just below for why this single map
 * replaced the old CHANNEL_TILE_VAR/CHANNEL_ICON_VAR pair.
 */
const CHANNEL_TINT_SLOT: Record<Channel, 'primary' | 'accent'> = {
  voice: 'primary',
  sms: 'accent',
  chat: 'primary',
}

/**
 * The channel tile's fill AND icon colour, together, always from the same
 * `--tint-X`/`--on-tint-X` pair — README §3 "Tile treatment: 28×28, radius 8,
 * tint background, icon 15px in the channel colour."
 *
 * FIX ROUND 2 (phase-2 whole-phase review, finding 2). This used to be two
 * parallel maps: `CHANNEL_TILE_VAR` picked the fill from the tint slots,
 * `CHANNEL_ICON_VAR` picked the icon from the `--channel-*` tokens — and
 * those two families agree by construction in Tossker (both ultimately trace
 * back to the same teal/gold hues) but disagree in Legacy, whose
 * `--channel-*` colours came from the old unread-pulse convention (voice =
 * warn orange, sms = ok green, chat = info blue) while its tints derive from
 * blue/orange. Computed: Legacy Day's sms tile was a green icon on an orange
 * tint at 1.56:1 — invisible. A caller could always update one map and
 * forget the other; this function makes that impossible by construction,
 * returning both halves from one lookup. `--on-tint-accent` needed its own
 * fix for this to clear the 3:1 icon minimum in Legacy Day (was 2.03:1
 * against `--tint-accent` — see `--legacy-warn-dim`'s comment in
 * tokens.css); every other theme already cleared it.
 *
 * Every consumer that paints a channel tile (`RailRow.tsx`'s main row and
 * stack sub-row, `Composer.tsx`'s channel chip, `ResultCard.tsx`'s result
 * tile, `CallFloor.tsx`'s live-other-caller tile) reads both properties from
 * here, never the two old maps separately.
 */
export function channelTileStyle(channel: Channel): CSSProperties {
  const slot = CHANNEL_TINT_SLOT[channel]
  return { background: `var(--tint-${slot})`, color: `var(--on-tint-${slot})` }
}

/**
 * The unread pulse, coloured by channel — Andrew's ask: "pulse a light green
 * for sms threads/blue for chat threads".
 *
 * Channel colour rather than one alert colour, so a glance at the rail says
 * both THAT something is waiting and WHAT it is waiting on. Voice takes warn:
 * a missed call is the one unread you cannot answer by typing.
 *
 * Design tokens, never hex — the repo forbids colour literals outside
 * tokens.css and is right to.
 */
export const UNREAD_DOT_CLASS: Record<Channel, string> = {
  sms: 'bg-ok',
  chat: 'bg-info',
  voice: 'bg-warn',
}

export const CHANNEL_LABEL: Record<Channel, string> = {
  voice: 'voice',
  sms: 'sms',
  chat: 'chat',
}

/**
 * Whether this thread is exactly the set `CallFloor.tsx`'s ringing/live-mine/
 * live-other/selected cards claim — README §3's "live voice lives on the
 * floor now" read literally: not just a relabelled band header, but the row
 * itself has exactly one home. `computeBand` (services/ops/src/priority.ts)
 * no longer short-circuits a live/ringing call to a retired band 1; without
 * this check it would fall through to the ordinary SLA-normalised wait check
 * like any other channel and land in band 2 or 3 — both still in
 * `BAND_ORDER` — producing a second, confusingly-labelled row (a call
 * actively being handled reading as "BREACHING SLA") for a thread the floor
 * above already shows correctly. `QueueRail`'s `bandGroups` excludes exactly
 * this set.
 *
 * Deliberately narrower than "every thread the floor shows": a missed call
 * or a call needing time logged also gets a floor card (README §2's "needs
 * something"), but those were already dual-shown — floor AND an ordinary
 * band 3/4 rail row — before this task, and nothing in this task's brief
 * asks that to change. Only the live/ringing case is new, because band 1 is
 * what used to keep it out of the band list; missed/unlogged never had that
 * protection and this task does not add it.
 */
export function isLiveVoice(thread: Pick<ThreadSummary, 'channel' | 'call'>): boolean {
  return thread.channel === 'voice' && thread.call !== null && (thread.call.state === 'ringing' || thread.call.state === 'live')
}

/**
 * Design order — Tossker repaint, README §3: "'LIVE NOW' disappears — live
 * voice lives on the floor now." Band 1 is gone from the rail's own grouping
 * (not just relabelled): `computeBand` (services/ops/src/priority.ts) no
 * longer ever assigns it, and even if some stale/cached row somehow still
 * carried a `band: 1`, `BAND_ORDER` no longer has a group for it, so
 * `QueueRail`'s `bandGroups` (which only iterates `BAND_ORDER`) would simply
 * never render it — belt and suspenders for the one thing this redesign is
 * most insistent about: live voice has exactly one home now, the call floor.
 */
export const BAND_ORDER: readonly Exclude<Band, 1>[] = [2, 3, 4]

export const BAND_LABEL: Record<Exclude<Band, 1>, string> = {
  2: 'BREACHING SLA',
  3: 'WAITING',
  4: 'NOT DEMANDING',
}

/**
 * The band header's leading icon — README §3: "triangle-alert `--red-500`
 * 'BREACHING SLA' · clock 'WAITING' · plain 'NOT DEMANDING'". Band 4 is
 * deliberately absent from this map (`plain` means no icon at all), so
 * callers must check for `undefined` rather than assume every band has one.
 */
export const BAND_ICON: Partial<Record<Exclude<Band, 1>, IconName>> = {
  2: 'triangle-alert',
  3: 'clock',
}

/** The band header's text (and icon) colour — danger for breaching, `--text-subtle` (`text-lo`) for not-demanding, per README §3. */
export const BAND_HEADER_TEXT_CLASS: Record<Exclude<Band, 1>, string> = {
  2: 'text-bad',
  3: 'text-mid',
  4: 'text-lo',
}

const LIVE_CLOCK_STATES: ReadonlySet<CallState> = new Set(['live', 'ringing'])

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Live calls (ringing/live) show their own ticking mm:ss clock; every other
 * row shows wait rounded to the coarsest sensible unit. Matches the seed
 * data's "0:38"/"1:12"/"0:06" (live calls) vs "18m"/"9m"/"2h"/"1d" (everything
 * else) — see docs/design-handoff/data/seed-data.json.
 */
export function waitLabel(thread: Pick<ThreadSummary, 'waitS' | 'call'>): string {
  const s = Math.max(0, Math.round(thread.waitS))
  if (thread.call !== null && LIVE_CLOCK_STATES.has(thread.call.state)) {
    return `${Math.floor(s / 60)}:${pad2(s % 60)}`
  }
  if (s < 3600) return `${Math.round(s / 60)}m`
  if (s < 86400) return `${Math.round(s / 3600)}h`
  return `${Math.round(s / 86400)}d`
}

export function livenessLabel(liveness: Liveness): string {
  switch (liveness) {
    case 'live':
      return 'live now'
    case 'online':
      return 'online'
    case 'ringing':
      return 'ringing'
    case 'over':
      return 'call over'
    default:
      return 'async'
  }
}

/** Only unknown/ambiguous threads carry a tier chip — every other tier speaks for itself via the rest of the row. */
export function tierChipLabel(tier: Tier): string | null {
  return tier === 'unknown' || tier === 'ambiguous' ? tier : null
}

/**
 * `held` is a queue (CRITICAL 1 fix — a single slot let a second arrival
 * bypass the hold entirely). The design's own copy, "1 new comm · <name> ·
 * click to place", already anticipates a count; with more than one queued,
 * a single name would be arbitrary, so the pill counts instead.
 */
export function heldPillLabel(held: ThreadSummary[]): string {
  if (held.length === 0) return ''
  if (held.length === 1) return `1 new comm · ${held[0]!.who}`
  return `${held.length} new comms`
}

/**
 * The row card's border — README §3: "Breaching = 1px `--red-100` + 3px
 * `--red-500` left rule. Selected = 1px `--teal-100` + 3px `--teal-700` left
 * rule + `--shadow-sm`." Selected wins over breaching when both are true
 * (the same precedence `CallFloor.tsx`'s own `SelectedCard` gives selection
 * over mine/someone-else) — a row you have open is never trying to also
 * alarm you about itself. Neither colour has a dedicated Tailwind border
 * utility (see `bg-bad-subtle`/`bg-cc-blue-pale`, which only cover
 * backgrounds), so this reaches for the raw token via inline style, the same
 * established pattern `CallFloor.tsx` uses throughout (task-3-report.md).
 */
export function rowBorderStyle(band: Band, selected: boolean): CSSProperties {
  if (selected) {
    return { border: '1px solid var(--brand-primary-soft)', borderLeft: '3px solid var(--brand-primary)' }
  }
  if (band === 2) {
    return { border: '1px solid var(--status-danger-soft)', borderLeft: '3px solid var(--status-danger)' }
  }
  return { border: '1px solid var(--border-subtle)' }
}

/** README §3: "Not-demanding rows drop to `opacity:.86`." An arbitrary-value Tailwind class — no token, just a fixed ratio. */
export function rowOpacityClass(band: Band): string {
  return band === 4 ? 'opacity-[.86]' : ''
}

/** README §3: "wait right, mono, tabular, `--red-500` when breaching else `--text-muted`." */
export function waitTextClass(band: Band): string {
  return band === 2 ? 'text-bad' : 'text-mid'
}

/**
 * README §3: "name 13px/600 (`--text-heading`; phone numbers in mono)". A
 * thread's `who` is a phone number rather than a resolved name for an
 * unmatched voice/SMS caller — this is a plain heuristic (a string that is
 * mostly digits/phone punctuation, long enough to be a real number), not a
 * parse of `endpoint`/E.164: `who` is free-form display text from wherever
 * the channel adapter got it, and there is no format guarantee to parse
 * strictly instead.
 */
const PHONE_LIKE = /^\+?[\d\s().-]{7,}$/

export function looksLikePhoneNumber(who: string): boolean {
  return PHONE_LIKE.test(who.trim())
}

export const TIER_CHIP_TONE = 'warn'
/**
 * Green like every other "good news, no action needed to notice it" token in
 * this rail (`bg-ok-subtle`/`text-ok` — see the design's own token set) — the
 * client saying they're done is a positive signal, not a warning.
 */
export const CLIENT_RESOLVED_CHIP_BG = 'bg-ok-subtle'
export const CLIENT_RESOLVED_CHIP_TEXT = 'text-ok'

/**
 * Whether to show the rail's "client says done" chip. A SIGNAL, never a
 * state — this thread is still open and still in the rail exactly as before;
 * see packages/shared/src/types.ts's `clientResolvedAt` doc comment and
 * services/ops/src/routes/client-resolve.ts for why nothing else changes.
 */
export function showClientResolvedChip(thread: Pick<ThreadSummary, 'clientResolvedAt'>): boolean {
  return !!thread.clientResolvedAt
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

/**
 * README §3's owner chip: "'Yours' or avatar + first name". `null` when the
 * thread is unowned — an unclaimed row simply carries no owner chip at all
 * (there is no "Unclaimed" treatment specified for the rail row itself; that
 * lives on the thread pane header per README §4/§8).
 */
export function ownerChipLabel(thread: Pick<ThreadSummary, 'ownerId' | 'owner'>, selfId: string | null): string | null {
  if (thread.ownerId !== null && thread.ownerId === selfId) return 'Yours'
  return thread.owner ? thread.owner.split(' ')[0]! : null
}

/**
 * README §3: "a '2 more on #4821' link ... where the stack chip was."
 * `stackCount` includes the row's own thread (see `QueueRail.tsx`'s
 * `stackCountFor`), so the link's own number is one fewer than the total.
 */
export function stackLinkLabel(ticket: string, stackCount: number): string {
  return `${Math.max(0, stackCount - 1)} more on ${ticket}`
}

/**
 * The multi-dispatcher clause — verbatim logic from `coverageNote()` in
 * Communicator Console Prototype.dc.html (line ~933), which the prototype's
 * own `railFooter` appends itself (line ~1558: `railFooter + ' ' +
 * this.coverageNote()`). `peers` here, like everywhere else in this module,
 * is the roster of *other* staff (see store.ts) — `covering` on each entry
 * is exactly what the prototype's `p.covering` reads.
 */
export function coverageNote(peers: Peer[]): string {
  const others = peers.filter((p) => p.covering)
  if (others.length === 0) return 'You are the only one covering.'
  const names = others.map((p) => p.name.split(' ')[0]).join(' and ')
  const verb = others.length > 1 ? 'are' : 'is'
  return `${names} ${verb} covering too. Nothing is locked or claimed — the badges just show where they are.`
}

/**
 * Per-role sort-rule copy plus the live `coverageNote()` — together this is
 * the prototype's `railFooter` computed property (Communicator Console
 * Prototype.dc.html line 1557-1558), amended for task 4 (Tossker repaint):
 * the original verbatim line was "...Live voice is a hard top band," which
 * stopped being true the moment band 1 was retired — live voice is not a
 * band at all any more, hard or otherwise, so restating it as one would be
 * actively wrong rather than merely stale. This function is not currently
 * rendered anywhere in the UI (see `QueueRail.tsx`'s own header comment on
 * why the standing footer paragraph was cut), but it is kept correct rather
 * than left to rot, on the chance a later screen (Settings' "Roles and
 * coverage", per README §7) wants this exact copy again.
 */
export function railFooter(role: StaffRole, peers: Peer[]): string {
  const sortRule =
    role === 'dispatch'
      ? 'Sorted in bands, then by SLA-normalised wait. Live voice lives on the call floor now, not a band.'
      : 'Intake stays readable. You are only notified on your own comms.'
  return `${sortRule} ${coverageNote(peers)}`
}

/* ------------------------------------------------------------------ *
 * CALL FLOOR (task 3, Tossker repaint) — docs/design-handoff/
 * tossker-redesign/README.md §2. Pure derivation only: CallFloor.tsx reads
 * `store.threads` (already band/sorted by the ops layer, same as the rest
 * of this rail) and hands it here; nothing below touches the store or does
 * I/O.
 * ------------------------------------------------------------------ */

/**
 * mm:ss, tabular. A near-duplicate of thread/format.ts's `formatClock` by
 * design, not by oversight: that module is pane B's (the open thread), this
 * one is pane A's (the rail), and the two directories deliberately do not
 * import from each other (see CHANNEL_ICON/CHANNEL_LABEL already living
 * separately from pane B's own channel handling). Five lines duplicated is
 * cheaper than a cross-pane coupling for a formatter this small.
 */
export function formatFloorClock(elapsedS: number): string {
  const s = Math.max(0, Math.floor(elapsedS))
  return `${Math.floor(s / 60)}:${pad2(s % 60)}`
}

/** "41m ago" / "2h ago" / "moments ago" — the floor's empty-state status line and the missed-call card's "missed 14m ago". */
export function agoLabel(elapsedMs: number): string {
  const s = Math.max(0, Math.round(elapsedMs / 1000))
  if (s < 60) return 'moments ago'
  if (s < 3600) return `${Math.round(s / 60)}m ago`
  if (s < 86400) return `${Math.round(s / 3600)}h ago`
  return `${Math.round(s / 86400)}d ago`
}

export type CallFloorCardKind = 'ringing' | 'live-mine' | 'live-other' | 'selected' | 'missed' | 'unlogged'

export interface CallFloorCard {
  kind: CallFloorCardKind
  thread: ThreadSummary
}

const FLOOR_CARD_ORDER: Record<CallFloorCardKind, number> = {
  ringing: 0,
  selected: 1,
  'live-mine': 1,
  'live-other': 1,
  missed: 2,
  unlogged: 2,
}

/**
 * Every card the floor renders, in design order (ringing, then live/selected,
 * then needs-something), derived only from facts `ThreadSummary` already
 * carries — task-3 brief binding decision 2: "who is on which call" is
 * scoped down to what the thread store already knows (the call, its state,
 * its duration, and the thread's owner if it has one). No presence field is
 * read here; `Peer` carries no per-technician call state and none is
 * invented.
 *
 * `selfId`/`activeId` decide only ONE branch each: a live call open in pane B
 * (`activeId`) renders as `selected` before anything else, matching README
 * §2's "Selected (the call open in pane B)" overriding mine/someone-else; a
 * live call this viewer owns (`selfId === thread.ownerId`) renders `live-mine`.
 * An unowned live call (`ownerId === null` — should not happen in practice,
 * since a ringing call is claimed before it can go live, but nothing here
 * assumes that) falls back to `live-other`, whose renderer omits the "X is on
 * it" line entirely rather than rendering a blank name — see CallFloor.tsx.
 */
/**
 * Which floor card a thread is, or `null` for one the floor does not hold.
 *
 * THE SINGLE RULE. `callFloorCards` and `isOnCallFloor` are both this
 * function, which is the point: the rail decides what to render by asking
 * whether the floor has it, and if the two ever answered differently a call
 * would appear twice — or, worse, vanish from both.
 *
 * `activeId`/`selfId` only pick BETWEEN live kinds. They never decide
 * membership, which is why `isOnCallFloor` can pass null for both and still
 * get a truthful answer.
 */
function callFloorKind(
  t: ThreadSummary,
  activeId: string | null,
  selfId: string | null,
): CallFloorCard['kind'] | null {
  if (t.channel !== 'voice' || t.call === null) return null
  const call = t.call
  if (call.state === 'ringing') return 'ringing'
  if (call.state === 'live') {
    if (t.id === activeId) return 'selected'
    if (t.ownerId !== null && t.ownerId === selfId) return 'live-mine'
    return 'live-other'
  }
  if (call.state === 'missed' && t.state !== 'closed') return 'missed'
  if (call.state === 'ended' && !call.dispoDone && t.state !== 'closed') return 'unlogged'
  // `ended` + already dispositioned, and every non-voice thread, are not the
  // floor's business — they belong to the queue rail below.
  return null
}

/**
 * Does the call floor hold this thread?
 *
 * ONE CALL, ONE CARD. Andrew: "Ended calls/active calls should have 1 card in
 * the left rail ever, and we shouldn't have to deal with them twice."
 *
 * The rail used to exclude only `isLiveVoice` — ringing and live — so a call
 * that had ended without being written up, or one that was missed, sat in the
 * floor's "to log" section AND again as an ordinary rail card. Two cards, one
 * call, and whichever you dealt with left the other behind.
 *
 * The floor keeps a call until it is dispositioned; the rail shows everything
 * the floor does not. Once the write-up is logged the call leaves the floor
 * and appears in the rail as ordinary history — still exactly one card.
 */
export function isOnCallFloor(t: ThreadSummary): boolean {
  return callFloorKind(t, null, null) !== null
}

export function callFloorCards(threads: ThreadSummary[], activeId: string | null, selfId: string | null): CallFloorCard[] {
  const cards: CallFloorCard[] = []
  for (const t of threads) {
    const kind = callFloorKind(t, activeId, selfId)
    if (kind !== null) cards.push({ kind, thread: t })
  }
  return cards.slice().sort((a, b) => FLOOR_CARD_ORDER[a.kind] - FLOOR_CARD_ORDER[b.kind])
}

export interface CallFloorCounts {
  ringing: number
  live: number
  missed: number
  unlogged: number
}

export function callFloorCounts(cards: CallFloorCard[]): CallFloorCounts {
  const counts: CallFloorCounts = { ringing: 0, live: 0, missed: 0, unlogged: 0 }
  for (const c of cards) {
    if (c.kind === 'ringing') counts.ringing += 1
    else if (c.kind === 'live-mine' || c.kind === 'live-other' || c.kind === 'selected') counts.live += 1
    else if (c.kind === 'missed') counts.missed += 1
    else counts.unlogged += 1
  }
  return counts
}

/**
 * The header row's middle summary — README §2's own example, "2 on calls ·
 * 1 ringing · 1 to call back" (Tossker Console.dc.html, B1's call-floor
 * header), extended the same way for an unlogged-time count since the
 * prototype gives no example with both present. The "no live calls · N to
 * finish" phrasing is verbatim from the "Only leftovers" B6 frame — used
 * only in that specific shape (nothing live or ringing, but leftovers
 * exist), never as a generic fallback.
 */
export function floorSummaryText(counts: CallFloorCounts): string {
  const { ringing, live, missed, unlogged } = counts
  if (live === 0 && ringing === 0) {
    const finishCount = missed + unlogged
    return finishCount === 0 ? 'quiet' : `no live calls · ${finishCount} to finish`
  }
  const parts: string[] = []
  if (live > 0) parts.push(`${live} on call${live === 1 ? '' : 's'}`)
  if (ringing > 0) parts.push(`${ringing} ringing`)
  if (missed > 0) parts.push(`${missed} to call back`)
  if (unlogged > 0) parts.push(`${unlogged} to log`)
  return parts.join(' · ')
}

/** The collapsed one-line strip's label — verbatim "no calls" from B6, or the same breakdown the open header would show. */
export function floorCollapsedLabel(counts: CallFloorCounts): string {
  const summary = floorSummaryText(counts)
  return summary === 'quiet' ? 'no calls' : summary
}

/**
 * The quiet empty state's "3CX connected · last call ended 41m ago" needs a
 * timestamp with nothing to compute it from except the threads the rail
 * already has — there is no dedicated "call ended at" field anywhere in
 * `ThreadSummary`/`CallFacts`. `lastActivity` is the closest honest proxy: it
 * is the thread's own last-touched time, and for a voice thread whose call
 * has already ended or been missed, that update IS the end of the call. Only
 * ended/missed calls count — a call still ringing or live has not "ended" by
 * any reading of that word.
 */
export function lastVoiceActivityAt(threads: ThreadSummary[]): string | null {
  let latest: string | null = null
  for (const t of threads) {
    if (t.channel !== 'voice' || t.call === null) continue
    if (t.call.state !== 'ended' && t.call.state !== 'missed') continue
    if (latest === null || t.lastActivity > latest) latest = t.lastActivity
  }
  return latest
}
